// RiskAnalysis.js
import { useEffect, useState, useCallback } from "react";
import { getPaginatedData } from "../utils";
import { getRiskNodeSets, getRiskSimilarity } from "../api/quontxApi";

import SidePanel from "../components/SidePanel";
import RiskProfile2oFilters from "../components/RiskProfile2oFilters";
import RadarPlotFilters from "../components/RiskRadarPlotFilters";
import RiskComparator1_1Filters from "../components/Comparator1_1Filters";
import RiskComparator1_NFilters from "../components/Comparator1_NFilters";
import RiskMutualFilters from "../components/RiskMutualFilters";

const OrgRiskAnalysisPage = () => {
  const [collapsed, setCollapsed] = useState(true);
  const [activeView, setActiveView] = useState("risk_mutual");
  const [displayData, setDisplayData] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const [domains, setDomains] = useState([]);

  // Memoize the fetch functions to prevent infinite loops
  const riskSimilarityFetch = useCallback(async (params) => {
    return await getRiskSimilarity({
      node_a: params.nodeA,
      node_b: params.nodeB
    });
  }, []);

  const riskSimilarity1NFetch = useCallback(async (params) => {
    return await getRiskSimilarity({
      node_a: params.nodeA,
      // node_b is not passed (empty)
    });
  }, []);

  const buttonClass = (key) =>
    `${
      activeView === key ? "bg-blue-800" : "bg-blue-500"
    } text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200`;

  const loadNodeSets = async () => {
    const nodeSets = await getPaginatedData(getRiskNodeSets, { itemsPerPage: 100 });

    setDomains(
      (nodeSets.data ?? []).slice().sort((a, b) => {
        if (a.description > b.description) return 1;
        if (a.description < b.description) return -1;
        return 0;
      })
    );
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      try {
        await loadNodeSets();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex relative">
      <SidePanel
        collapseCallback={() => setCollapsed(!collapsed)}
        collapsed={collapsed}
        data={displayData}
      />

      <div className="grow">
        <div className="p-6 pb-0">
          <h1 className="mb-6 text-center">Org Risk Analysis</h1>

          <div className="w-full flex flex-col items-center content-center overflow-y-auto custom-scrollbar h-[83vh]">
            <div className="flex flex-col items-center content-center gap-6 w-full">
              {/* Buttons */}
              <div className="flex gap-4 items-center content-center">
                <button
                  onClick={() => setActiveView("risk_mutual")}
                  className={buttonClass("risk_mutual")}
                >
                  Org Risk Mutual
                </button>

                <button
                  onClick={() => setActiveView("risk_similarity_1_1")}
                  className={buttonClass("risk_similarity_1_1")}
                >
                  Org Risk Similarity 1-1
                </button>

                <button
                  onClick={() => setActiveView("risk_similarity_1_N")}
                  className={buttonClass("risk_similarity_1_N")}
                >
                  Org Risk Similarity 1-N
                </button>

                <button
                  onClick={() => setActiveView("risk_profile")}
                  className={buttonClass("risk_profile")}
                >
                  Org Risk Profile 1o
                </button>

                <button
                  onClick={() => setActiveView("risk_profile_2o")}
                  className={buttonClass("risk_profile_2o")}
                >
                  Org Risk Profile 2o
                </button>
              </div>

              {/* Active component */}
              {activeView === "risk_mutual" && (
                <RiskMutualFilters
                  propagnateData={(value) => setDisplayData(value)}
                  domains={domains}
                  isLoading={isLoading}
                />
              )}

              {activeView === "risk_similarity_1_1" && (
                <RiskComparator1_1Filters
                  propagnateData={(value) => setDisplayData(value)}
                  isLoading={isLoading}
                  fetchDataFunction={riskSimilarityFetch}
                />
              )}

              {activeView === "risk_similarity_1_N" && (
                <RiskComparator1_NFilters
                  propagnateData={(value) => setDisplayData(value)}
                  isLoading={isLoading}
                  fetchDataFunction={riskSimilarity1NFetch}
                />
              )}

              {activeView === "risk_profile" && (
                <RadarPlotFilters
                  propagnateData={(value) => setDisplayData(value)}
                  isLoading={isLoading}
                />
              )}

              {activeView === "risk_profile_2o" && (
                <RiskProfile2oFilters
                  propagnateData={(value) => setDisplayData(value)}
                  isLoading={isLoading}
                />
              )}

              {!domains?.length && !isLoading && (
                <p className="text-sm opacity-70">No domains loaded.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgRiskAnalysisPage;